import type { ReactNode } from "react";
import type { DrawerType } from "reducers/drawer";
import { calculateAge } from "utils/date-utils";

export type DrawerAction = {
  type: "drawer";
  drawerType: DrawerType;
};

export type CustomAction = {
  type: "custom";
  handler: () => void;
};

export type InfoPillAction = DrawerAction | CustomAction;

export type InfoPillData = {
  value: string | undefined | ReactNode;
  classNames?: string;
  action?: InfoPillAction;
  tooltip?: string;
  tooltipHtml?: boolean; // whether tooltip content is HTML
  disableTooltip?: boolean; // disable the outer tooltip wrapper (useful when value has its own tooltips)
};

export type InfoPillFields = Record<string, InfoPillData>;

export const NOT_AVAILABLE = "Not Available";
export const NONE = "None";

// helper Functions
export const formatClaimantName = (claimant?: {
  first_name: string;
  last_name: string;
}): string => {
  return claimant
    ? `${claimant.last_name}, ${claimant.first_name}`
    : NOT_AVAILABLE;
};

export const getEmployerName = (data: {
  employer?: { name: string } | null;
}): string => {
  return data?.employer?.name || NOT_AVAILABLE;
};

// display DOIs in a csv format, if there's only one, just display the date
export const getDatesOfInjuryInfo = (
  dates?: Array<{ date: string }> | string,
) => {
  if (!dates) return { label: "Date of injury", value: NOT_AVAILABLE };

  if (typeof dates === "string") {
    return {
      label: "Date of injury",
      value: dates,
    };
  }

  return {
    label: `Date${dates.length > 1 ? "s" : ""} of injury`,
    value: dates.map((doi) => doi.date).join(", ") || NOT_AVAILABLE,
  };
};

export const formatDateOfBirth = (birthDate?: string | Date): string => {
  if (!birthDate) return NOT_AVAILABLE;

  try {
    const date = new Date(birthDate);
    // check if the date is valid
    if (isNaN(date.getTime())) return NOT_AVAILABLE;

    const formattedDate = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    const age = calculateAge(date);
    return age !== null ? `${formattedDate} (Age ${age})` : formattedDate;
  } catch {
    return NOT_AVAILABLE;
  }
};

export const formatSSN = (ssn?: string): string => {
  if (!ssn) return NOT_AVAILABLE;

  // Remove any non-numeric characters
  const cleanSSN = ssn.replace(/\D/g, "");

  // Check if we have 9 digits
  if (cleanSSN.length !== 9) return ssn; // Return original if not standard format

  // Format as XXX-XX-XXXX
  return `${cleanSSN.slice(0, 3)}-${cleanSSN.slice(3, 5)}-${cleanSSN.slice(5)}`;
};

export const formatMedicareEligibilityStatus = (
  isMedicareEligible?: boolean | null,
): string => {
  if (isMedicareEligible === null || isMedicareEligible === undefined) {
    return "Unknown";
  }
  return isMedicareEligible ? "Eligible" : "Not Eligible";
};

// utility function to get claim numbers formatted by DOI for tooltips
export const getClaimNumbersString = (
  claimNumbers?: Array<{ number: string; date_of_injury_id?: number }>,
  dateOfInjuries?: Array<{ id: number; date: string }>,
): string => {
  if (!claimNumbers || claimNumbers.length === 0) return NONE;
  if (!dateOfInjuries || dateOfInjuries.length === 0) {
    // fallback to just claim numbers if no DOI data
    return claimNumbers.map((claim) => claim.number).join(", ");
  }

  // group claim numbers by DOI
  const claimsByDoi = claimNumbers.reduce(
    (acc, claim) => {
      const doiId = claim.date_of_injury_id;
      if (doiId) {
        if (!acc[doiId]) acc[doiId] = [];
        acc[doiId].push(claim.number);
      }
      return acc;
    },
    {} as Record<number, string[]>,
  );

  // format as "[DOI Date] - claim1, claim2, claim3"
  const formattedEntries = dateOfInjuries
    .filter((doi) => claimsByDoi[doi.id]) // only include DOIs that have claims
    .map((doi) => {
      const claims = claimsByDoi[doi.id];
      const dateFormatted = doi.date.substring(0, 10); // get YYYY-MM-DD format
      return `[${dateFormatted}] - ${claims.join(", ")}`;
    });

  return formattedEntries.length > 0 ? formattedEntries.join("<br />") : NONE;
};
